import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { SupportedInstitution } from '@app/finxone-web-frontend/app/lib/services/savings-account-service/savings-account.type';
import { scrollLoading } from '@app/finxone-web-frontend/app/lib/utils/scrolling-utils/scrolling.utils';
import { buildHandleBarModel } from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  BaseWidgetProperties,
  ProviderClass,
  WidgetNames,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, takeUntil } from 'rxjs';
import { PageConfigurationProvider } from '../../../zones/page-configuration.provider';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-banks-listing',
  templateUrl: './banks-listing.component.html',
  styleUrl: './banks-listing.component.scss',
})
export class BanksListingComponent implements OnInit, AfterViewInit {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  @Output() selectedBankEmit = new EventEmitter<SupportedInstitution>();
  @Output() scrolled = new EventEmitter<void>();
  public fetchingTemplate = '';
  public itemsPerRow = 1;
  @Input() set widgetConfigData(widgetConfig: BaseWidgetProperties) {
    this.widgetConfig = widgetConfig;
    this.itemsPerRow = widgetConfig?.itemsPerRow ?? 1;
    this.setMissingDataIfNeeded();
  }
  public widgetConfig: BaseWidgetProperties;
  @Input() contentStyle: baseCssConfigStyle;
  @Input() searchIconStyle: baseCssConfigStyle;
  @Input() textContentStyle: baseCssConfigStyle;
  @Input() set setSupportedInstitutions(supportedInstitutions: SupportedInstitution[]) {
    this.supportedInstitutions = supportedInstitutions;
    this.filteredList = supportedInstitutions;
  }
  @Input() navigateToUrl: string;
  @Input() public widgetName: WidgetNames;
  @Input() totalListCount: number;
  @Input() isApiInProgress = false;
  public containerHeight: string;
  public currentStyle: baseCssConfigStyle;
  public content = '';
  public noRecordsTemplate = '';
  public searchText = '';
  public baseHandleBarModel: any;
  public activeBankIndex = 0;
  public templateFunction: HandlebarsTemplateDelegate<any>;
  public noRecordsTemplateFunction: HandlebarsTemplateDelegate<any>;
  public fetchingTemplateFunction: HandlebarsTemplateDelegate<any>;
  public pageNumber = 1;
  public filteredList: SupportedInstitution[] = [];
  public supportedInstitutions: SupportedInstitution[] = [];

  constructor(
    private store: Store,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private el: ElementRef,
    public pageConfig: PageConfigurationProvider,
    public dataIn: ProviderClass<any>,
    public configService: ConfigService,
  ) {}

  ngOnInit(): void {
    this.updateStyleProperties();
    this.initHandleBars();
  }

  ngAfterViewInit() {
    this.containerHeight = scrollLoading('bank-listing', this.el);
  }

  private updateStyleProperties() {
    this.contentStyle = {
      ...this.contentStyle,
      display: 'block',
    };
  }

  private initHandleBars() {
    this.filteredList = this.supportedInstitutions;
    this.content = this.widgetConfig?.textContent;
    this.noRecordsTemplate = this.dataIn?.attri?.noRecordsTemplate ?? '';
    this.fetchingTemplate = this.widgetConfig?.['loadingTextContent'] ?? '';
    this.templateFunction = Handlebars.compile(this.content);
    this.noRecordsTemplateFunction = Handlebars.compile(this.noRecordsTemplate);
    this.fetchingTemplateFunction = Handlebars.compile(this.fetchingTemplate);
    buildHandleBarModel(this.store, '', this.countriesCurrenciesService, this.iconCacheService)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.baseHandleBarModel = model;
      });
  }

  public searchBanks(event: any) {
    this.searchText = '';
    if (event.target.value.length) {
      if (!this.filteredList.length) this.filteredList = this.supportedInstitutions;
      this.filteredList = this.supportedInstitutions.filter((v) =>
        v.name.toLowerCase().includes(event.target.value.toLowerCase()),
      );
      this.searchText = event.target.value;
    } else {
      this.filteredList = this.supportedInstitutions;
    }
  }

  public createTemplateData(supportedInstitution: SupportedInstitution, index: number) {
    return this.templateFunction({
      ...this.baseHandleBarModel,
      index,
      lastItem: index === this.supportedInstitutions.length - 1,
      bankName: supportedInstitution.name,
      bankBic: supportedInstitution.bic,
      bankLogo: supportedInstitution.logo,
      maxAccessValidForDays: supportedInstitution.max_access_valid_for_days,
      maxAccessValidForDaysReconfirmation: supportedInstitution.max_access_valid_for_days_reconfirmation,
      transactionTotalDays: supportedInstitution.transaction_total_days,
    });
  }

  public renderNoRecordsTemplate() {
    return this.noRecordsTemplateFunction({
      ...this.baseHandleBarModel,
    });
  }

  public renderFetchingTemplate() {
    return this.fetchingTemplateFunction({
      ...this.baseHandleBarModel,
    });
  }

  public proceedWithBank(bankInfo: SupportedInstitution) {
    this.selectedBankEmit.emit(bankInfo);
  }

  public fetchNewBanks(bankIndex: number) {
    const isLastIndex = bankIndex === this.supportedInstitutions.length - 1;
    if (isLastIndex) {
      this.scrolled.emit();
    }
  }

  public activateWindowScroll(event: Event) {
    const element = event.target as HTMLElement;
    if (Math.round(element.scrollHeight - element.scrollTop) === element.clientHeight) {
      window.scrollBy(0, 100);
    }
    if (element.scrollTop === 0) {
      window.scrollBy(0, -100);
    }
  }

  private setMissingDataIfNeeded() {
    if (
      this.widgetConfig.inputFieldSettings &&
      !this.widgetConfig?.inputFieldSettings?.css_config?.['inputWidth']
    ) {
      this.widgetConfig.inputFieldSettings.css_config['inputWidth'] = 100;
    }
    if (
      this.widgetConfig.inputFieldSettings &&
      !this.widgetConfig?.inputFieldSettings?.css_config?.['inputPosition']
    ) {
      this.widgetConfig.inputFieldSettings.css_config['inputPosition'] = 'start';
    }
  }

  public onPageChange(event: { first: number; page: number; pageCount: number; rows: number }) {
    this.pageNumber = event.first / event.rows + 1;
    this.dataIn.attri['pageSize'] = event.rows;
    // Dispatch action to get banks with pagination if needed
  }

  public searchValue(searchText: string) {
    this.searchText = searchText;
    this.pageNumber = 1;
    // Dispatch action to search banks if needed
  }

  public get pageSize(): number {
    if (this.dataIn.attri['pageSize'] != null) return Number(this.dataIn.attri['pageSize']);
    else return 10;
  }

  public get gridStyles(): Record<string, string> {
    return {
      'grid-template-columns': 'repeat(' + this.itemsPerRow + ', minmax(0, 1fr))',
    };
  }
}
